import { Badge, Box, Card, Flex, Heading, IconButton, Text } from '@radix-ui/themes'
import { EyeOpenIcon, FileIcon, TrashIcon } from '@radix-ui/react-icons'
import { PamDocumentSummary } from '@Generated/back-office-client/src'
import { FileUploader, FileWithPreview } from '@Components/common/file-uploader'

const ACCEPT_TYPES = {
  'image/jpeg': ['.jpeg', '.jpg'],
  'image/png': ['.png'],
  'application/pdf': ['.pdf'],
}

export type PamVerificationDocumentsProps = {
  documents: PamDocumentSummary[]
  canWrite: boolean
  isUploading: boolean
  uploadError: string | null
  onUpload: (file: File) => void
  onPreview: (document: PamDocumentSummary) => void
  onDelete: (document: PamDocumentSummary) => void
}

export const PamVerificationDocuments = ({
  documents,
  canWrite,
  isUploading,
  uploadError,
  onUpload,
  onPreview,
  onDelete,
}: PamVerificationDocumentsProps) => {
  const tiles = documents.map((document) => {
    return (
      <Card key={document.id} style={{ width: 180 }}>
        <Flex direction="column" gap="2" align="center">
          <FileIcon width="40" height="40" />
          <Text size="1" truncate style={{ maxWidth: 150 }}>
            {document.fileName}
          </Text>
          <Flex gap="2">
            <IconButton
              size="1"
              variant="soft"
              aria-label="Preview document"
              onClick={() => onPreview(document)}>
              <EyeOpenIcon />
            </IconButton>
            {canWrite && (
              <IconButton
                size="1"
                variant="soft"
                color="red"
                aria-label="Delete document"
                onClick={() => onDelete(document)}>
                <TrashIcon />
              </IconButton>
            )}
          </Flex>
        </Flex>
      </Card>
    )
  })

  return (
    <Box>
      <Flex align="center" gap="2" mb="2">
        <Heading size="4">Documents</Heading>
        <Badge variant="soft">{documents.length}</Badge>
      </Flex>

      {documents.length === 0 ? (
        <Text color="gray" size="2">
          No documents uploaded.
        </Text>
      ) : (
        <Flex gap="3" wrap="wrap">
          {tiles}
        </Flex>
      )}

      {canWrite && (
        <Box mt="3" style={{ maxWidth: 420 }}>
          <FileUploader
            formLabel="Upload proof of verification (JPEG, PNG, or PDF)"
            acceptTypes={ACCEPT_TYPES}
            showImageAsPreview={false}
            errorMessage={uploadError}
            onFileDropped={(dropped: FileWithPreview) => onUpload(dropped.file)}
          />
          {isUploading && (
            <Text size="1" color="gray">
              Uploading…
            </Text>
          )}
        </Box>
      )}
    </Box>
  )
}
